import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Trash2, Package, Users, Phone, Mail, MapPin, Truck, CheckCircle, XCircle, Clock } from 'lucide-react';
import { useSale, useDeleteSale, useUpdateSale } from '../../hooks/useSales';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { formatCurrency, formatDate } from '../../lib/utils';
import { toast } from 'sonner';

const getEstadoBadge = (estado: string) => {
  const variants: Record<string, { variant: 'success' | 'danger' | 'warning' | 'info' | 'default'; label: string }> = {
    pendiente: { variant: 'warning', label: 'Pendiente' },
    completada: { variant: 'success', label: 'Completada' },
    cancelada: { variant: 'danger', label: 'Cancelada' },
  };
  const config = variants[estado] || variants.pendiente;
  return <Badge variant={config.variant}>{config.label}</Badge>;
};

const getEstadoIcon = (estado: string) => {
  switch (estado) {
    case 'pendiente': return <Clock size={16} className="text-yellow-500" />;
    case 'completada': return <CheckCircle size={16} className="text-green-500" />;
    case 'cancelada': return <XCircle size={16} className="text-red-500" />;
    default: return null;
  }
};

export default function SaleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const ventaId = id ? parseInt(id) : 0;

  const { data: venta, isLoading, refetch } = useSale(ventaId);
  const deleteSale = useDeleteSale();
  const updateSale = useUpdateSale();

  const handleDelete = async () => {
    if (!venta) return;
    
    if (venta.estado !== 'completada' && venta.estado !== 'cancelada') {
      toast.warning('Solo se pueden eliminar ventas en estado completada o cancelada');
      return;
    }

    const mensaje = venta.estado === 'completada'
      ? `¿Estás seguro de eliminar la venta ${venta.numeroVenta}? Se restaurará el stock.`
      : `¿Estás seguro de eliminar la venta ${venta.numeroVenta}?`;

    if (!confirm(mensaje)) return;

    try {
      await deleteSale.mutateAsync(ventaId);
      navigate('/ventas');
    } catch (error) {
      toast.error('Error al eliminar la venta');
    }
  };

  const handleChangeEstado = async (nuevoEstado: string) => {
    if (!confirm(`¿Cambiar estado a "${nuevoEstado}"?`)) return;
    try {
      await updateSale.mutateAsync({
        id: ventaId,
        data: { estado: nuevoEstado },
      });
      toast.success(`Estado actualizado a "${nuevoEstado}"`);
      refetch();
    } catch (error) {
      toast.error('Error al actualizar el estado');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando venta...</div>
      </div>
    );
  }

  if (!venta) {
    return <div className="text-center py-8 text-red-500">Venta no encontrada</div>;
  }

  const puedeEditar = venta.estado === 'pendiente';
  const puedeCancelar = venta.estado === 'pendiente';
  const puedeCompletar = venta.estado === 'pendiente';

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/ventas')} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-800">{venta.numeroVenta}</h1>
              {getEstadoBadge(venta.estado)}
            </div>
            <p className="text-sm text-gray-500">
              {venta.clienteNombre} • {formatDate(venta.fechaVenta)}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {puedeEditar && (
            <Button variant="outline" onClick={() => navigate(`/ventas/editar/${venta.id}`)}>
              <Edit size={16} className="mr-2" />
              Editar
            </Button>
          )}
          {puedeCompletar && (
            <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={() => handleChangeEstado('completada')}>
              <CheckCircle size={16} className="mr-2" />
              Completar
            </Button>
          )}
          {puedeCancelar && (
            <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleChangeEstado('cancelada')}>
              <XCircle size={16} className="mr-2" />
              Cancelar
            </Button>
          )}
          <Button
            variant="outline"
            className="text-red-600 border-red-200 hover:bg-red-50"
            onClick={handleDelete}
            disabled={venta.estado !== 'completada' && venta.estado !== 'cancelada'}
          >
            <Trash2 size={16} className="mr-2" />
            Eliminar
          </Button>
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Cliente */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Users size={20} className="text-blue-500" />
              Datos del Cliente
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Columna Izquierda */}
              <div className="space-y-2">
                <div>
                  <p className="text-sm text-gray-500">Nombre</p>
                  <p className="font-medium text-gray-800">{venta.clienteNombre}</p>
                </div>
                {venta.clienteTelefono && (
                  <div>
                    <p className="text-sm text-gray-500">Teléfono</p>
                    <a href={`tel:${venta.clienteTelefono}`} className="font-medium text-blue-600 hover:underline flex items-center gap-1">
                      <Phone size={14} />
                      {venta.clienteTelefono}
                    </a>
                  </div>
                )}
                {venta.clienteEmail && (
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <a href={`mailto:${venta.clienteEmail}`} className="font-medium text-blue-600 hover:underline flex items-center gap-1">
                      <Mail size={14} />
                      {venta.clienteEmail}
                    </a>
                  </div>
                )}
              </div>
              {/* Columna Derecha */}
              <div className="space-y-2">
                {venta.clienteDocumento && (
                  <div>
                    <p className="text-sm text-gray-500">Documento</p>
                    <p className="font-medium text-gray-800">{venta.clienteDocumento}</p>
                  </div>
                )}
                {venta.clienteLocalidad && (
                  <div>
                    <p className="text-sm text-gray-500">Localidad</p>
                    <p className="font-medium text-gray-800 flex items-center gap-1">
                      <MapPin size={14} className="text-gray-400" />
                      {venta.clienteLocalidad}
                    </p>
                  </div>
                )}
                {venta.clienteDireccion && (
                  <div>
                    <p className="text-sm text-gray-500">Dirección</p>
                    <p className="font-medium text-gray-800 flex items-center gap-1">
                      <MapPin size={14} className="text-gray-400" />
                      {venta.clienteDireccion}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Productos */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <Package size={20} className="text-blue-500" />
              Productos
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-sm text-gray-500 border-b">
                    <th className="pb-2">Producto</th>
                    <th className="pb-2 text-center">Cantidad</th>
                    <th className="pb-2 text-right">Precio</th>
                    <th className="pb-2 text-right">Subtotal</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {venta.detalles?.map((detalle: any, index: number) => (
                    <tr key={index} className="text-sm">
                      <td className="py-2">
                        <p className="font-medium text-gray-800">{detalle.producto?.nombre || 'Producto'}</p>
                        {detalle.producto?.sku && (
                          <p className="text-xs text-gray-400">SKU: {detalle.producto.sku}</p>
                        )}
                      </td>
                      <td className="py-2 text-center">{detalle.cantidad}</td>
                      <td className="py-2 text-right">{formatCurrency(detalle.precioUnitario)}</td>
                      <td className="py-2 text-right">{formatCurrency(detalle.subtotal)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t font-bold">
                    <td colSpan={3} className="py-2 text-right">Total</td>
                    <td className="py-2 text-right text-blue-600">{formatCurrency(venta.total ?? 0)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>
        </div>

        {/* Columna derecha */}
        <div className="space-y-6">
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Resumen</h2>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Subtotal</span>
                <span>{formatCurrency(venta.subtotal)}</span>
              </div>
              {venta.descuento > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Descuento</span>
                  <span>-{formatCurrency(venta.descuento)}</span>
                </div>
              )}
              {venta.costoEnvio > 0 && (
                <div className="flex justify-between text-sm text-blue-600">
                  <span>Costo de Envío</span>
                  <span>+{formatCurrency(venta.costoEnvio)}</span>
                </div>
              )}
              {venta.comision > 0 && (
                <div className="flex justify-between text-sm text-purple-600">
                  <span>Comisión ({venta.comision}%)</span>
                  <span>+{formatCurrency((venta.subtotal * venta.comision) / 100)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm pt-2 border-t">
                <span className="font-semibold">Total</span>
                <td className="py-2 text-right text-blue-600">{formatCurrency(venta.total ?? 0)}</td>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Método de Pago</span>
                <span className="font-medium capitalize">{venta.metodoPago}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Estado</span>
                <span className="font-medium flex items-center gap-1">
                  {getEstadoIcon(venta.estado)}
                  {venta.estado}
                </span>
              </div>
              {venta.observaciones && (
                <div className="pt-2 border-t">
                  <p className="text-sm text-gray-500">Observaciones</p>
                  <p className="text-sm text-gray-600">{venta.observaciones}</p>
                </div>
              )}
            </div>
          </Card>

          {/* Envío */}
          {venta.envio && (
            <Card className="p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Truck size={20} className="text-blue-500" />
                Envío
              </h2>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Número</span>
                  <span className="font-medium">{venta.envio.numeroEnvio}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Estado</span>
                  <span className="font-medium capitalize">{venta.envio.estado}</span>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}